<template>
  <div class="relative">
    <div class="data-container max-h-96 overflow-auto bg-white shadow rounded-lg work-area-bg">
      <!-- Editable view uses default (previewMode = false) -->
      <TicketInformation />
    </div>

    <!-- Button to open modal -->
    <div class="flex justify-center pt-2">
      <button
        :disabled="!isAuthenticated"
        :class="{
          'px-4 py-2 bg-gray-500 text-white rounded': true,
          'hover:bg-cyan-500': isAuthenticated,
          'opacity-50 cursor-not-allowed': !isAuthenticated
        }"
        @click="openModal"
      >
        View Report Preview
      </button>
    </div>

    <!-- Modal -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50"
      @click.self="closeModal"
    >
      <div
        :class="['bg-white p-6 rounded shadow-lg w-letter overflow-auto', hasRequiredData ? 'h-full' : 'h-auto']"
      >
        <h3 class="text-lg font-bold mb-4 text-center">
          Report Preview
        </h3>

        <div
          v-if="hasRequiredData"
          id="contentToCapture"
          ref="contentToCapture"
          class="bg-white relative work-area-bg"
        >
          <!-- Header -->
          <div class="px-7 py-3 flex justify-center relative items-center">
            <img
              src="../assets/logo.png"
              alt="Perbyte Logo"
              class="h-16 mr-4"
            >
            <div class="p-1 mt-1 border-gray-200 w-full">
              <h2 class="text-lg text-center pl-5 font-semibold text-gray-700">
                Status Report for: {{ selectedCompanyLabel }}
              </h2>
            </div>
          </div>

          <!-- Body -->
          <div class="p-7">
            <p class="text-md font-medium text-gray-600 mb-2">
              {{ selectedFrequencyLabel }} reports are generated for your convenience.
            </p>
            <p class="text-md font-medium text-gray-600 mb-2">
              Report for the period of: {{ formattedStartDate }} to {{ formattedEndDate }}
            </p>

            <!-- Ticket Information in PDF mode when capturing -->
            <div class="mb-4">
              <TicketInformation
                :preview-mode="true"
                :pdf-mode="isPdfMode"
              />
            </div>

            <!-- Period Summary -->
            <div class="flex justify-between bg-gray-100 p-4 rounded-lg text-gray-700 font-semibold mb-4 avoid-page-break">
              <div>Hours Budgeted: <span class="font-bold">{{ formattedTotalBudgeted }}h</span></div>
              <div>Used this Quarter(s): <span class="font-bold">{{ formattedUsedThisQuarter }}h</span></div>
              <div>Used this Date Range: <span class="font-bold">{{ formattedUsedThisPeriod }}h</span></div>
              <div>Hours Remaining: <span class="font-bold">{{ formattedRemainingHours }}h</span></div>
            </div>

            <!-- Chart -->
            <div class="w-full avoid-page-break">
              <ChartDisplay
                :key="renderKey"
                ref="chartDisplay"
              />
              <span class="sr-only">Preview chart key: {{ renderKey }}</span>
            </div>
          </div>
        </div>

        <!-- No data fallback -->
        <div
          v-else
          class="flex items-center justify-center"
        >
          <div class="bg-gray-100 p-4 rounded-lg shadow-lg text-center">
            <p class="text-sm font-medium text-gray-600">
              Please select a company, frequency, and date range to generate a report.
            </p>
          </div>
        </div>

        <!-- Modal buttons -->
        <div class="flex justify-center pt-2">
          <button
            class="mx-2 mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-700"
            @click="closeModal"
          >
            Close Preview
          </button>
          <button
            v-if="hasRequiredData"
            class="mx-2 mt-4 px-4 py-2 bg-cyan-500 text-white rounded hover:bg-cyan-700"
            @click="downloadPDF"
          >
            Download Report
          </button>
        </div>
      </div>
    </div>

    <!-- Post-export clear notes prompt -->
    <div
      v-if="showClearNotesPrompt"
      class="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-[70]"
    >
      <div class="bg-white p-6 rounded-lg shadow-lg max-w-sm mx-auto border border-gray-100">
        <h4 class="text-lg font-semibold text-gray-900">
          Clear Notes
        </h4>
        <p class="mt-2 text-sm text-gray-600">
          Do you want to clear notes after report generation?
        </p>
        <div class="mt-6 flex justify-center gap-3">
          <button
            type="button"
            class="bg-gray-300 hover:bg-gray-400 text-black py-2 px-4 rounded"
            @click="handleClearNotesDecision(false)"
          >
            No
          </button>
          <button
            type="button"
            class="py-2 px-4 rounded"
            style="background-color: #0891b2; color: #ffffff;"
            @mouseover="$event.currentTarget.style.backgroundColor = '#0e7490'"
            @mouseout="$event.currentTarget.style.backgroundColor = '#0891b2'"
            @click="handleClearNotesDecision(true)"
          >
            Yes
          </button>
        </div>
        <label class="mt-4 flex items-center justify-center gap-2 text-sm text-gray-700">
          <input
            v-model="dontShowClearNotesAgain"
            type="checkbox"
            class="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
          >
          <span>Don't show this again</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick, watch, ref } from 'vue';
import { useRoute } from 'vue-router';
import html2pdf from 'html2pdf.js';
import TicketInformation from './TicketInformation.vue';
import ChartDisplay from './ChartDisplay.vue';
import { useAuthStore } from '../pinia/authStore';
import { useDsaReportsStore } from '../pinia/dsaReportsStore';
import { useDsaReportsPeriodsStore } from '../pinia/dsaReportsPeriodsStore';

const CLEAR_NOTES_PREF_KEY = 'dsaClearNotesPreference';

export default {
  name: 'ScrollableDataContainer',
  components: { TicketInformation, ChartDisplay },
  setup() {
    const route = useRoute();
    const renderKey = ref(0);
    const notesStore = useDsaReportsStore();
    const authStore  = useAuthStore();
    const periodStore = useDsaReportsPeriodsStore();

    watch(
      () => notesStore.selectedContract,
      (newContract) => {
        const contract = notesStore.contracts.find((c) => c.id === newContract);
        if (contract?.frequency) {
          notesStore.selectedFrequency = contract.frequency.id;
        }
      }
    );

    watch(
      () => route.fullPath,
      (nextPath, prevPath) => {
        renderKey.value += 1;
        console.debug('[ScrollableDataContainer] route change', { prevPath, nextPath, renderKey: renderKey.value });
      }
    );

    return { notesStore, authStore, periodStore, renderKey };
  },
  data() {
    return {
      isModalOpen: false,
      isPdfMode: false,
      showClearNotesPrompt: false,
      dontShowClearNotesAgain: false
    };
  },
  computed: {
    myStore() {
      return useDsaReportsStore();
    },
    selectedCompany() {
      return this.myStore.selectedContract;
    },
    selectedFrequency() {
      return this.myStore.selectedFrequency;
    },
    startDate() {
      return this.myStore.startDate;
    },
    endDate() {
      return this.myStore.endDate;
    },
    formattedStartDate() {
      return this.myStore.formattedStartDate;
    },
    formattedEndDate() {
      return this.myStore.formattedEndDate;
    },
    selectedCompanyLabel() {
      return this.myStore.selectedContractName;
    },
    selectedFrequencyLabel() {
      return this.myStore.selectedFrequencyLabel;
    },
    formattedTotalBudgeted() {
      return this.periodStore.totalBudgeted.toFixed(2);
    },
    formattedUsedThisQuarter() {
      return this.periodStore.usedThisQuarter.toFixed(2);
    },
    formattedUsedThisPeriod() {
      return this.periodStore.usedThisPeriod.toFixed(2);
    },
    formattedRemainingHours() {
      return this.periodStore.remainingHours.toFixed(2);
    },
    isAuthenticated() {
      return this.authStore.isAuthenticated;
    },
    hasRequiredData() {
      return this.selectedCompany && this.selectedFrequency && this.startDate && this.endDate;
    }
  },
  methods: {
    openModal() {
      this.isModalOpen = true;
      this.$nextTick(() => {
        this.$refs.chartDisplay?.refreshChart();
      });
    },
    closeModal() {
      this.isModalOpen = false;
    },
    async downloadPDF() {
      this.isPdfMode = true;
      try {
        await nextTick();

        const element = this.$refs.contentToCapture;
        const scaleFactor = 1.25;
        const pdfWidth  = 816 * scaleFactor;
        const pdfHeight = 1056 * scaleFactor;
        const options = {
          margin: [20,20,20,20],
          filename: this.generateFileName(),
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, scrollY: -window.scrollY },
          jsPDF: { unit: 'px', format: [pdfWidth, pdfHeight], orientation: 'portrait' },
          pagebreak: { mode: ['css','legacy'] }
        };

        await html2pdf().set(options).from(element).save();
        this.myStore.setLastReportParams();
        this.handlePostExportNotes();
      } finally {
        this.isPdfMode = false;
      }
    },
    handlePostExportNotes() {
      const preference = this.getClearNotesPreference();
      if (preference?.skipPrompt) {
        if (preference.clearNotes) {
          this.myStore.clearReportNotes();
        }
        return;
      }
      this.showClearNotesPrompt = true;
    },
    handleClearNotesDecision(shouldClear) {
      if (shouldClear) {
        this.myStore.clearReportNotes();
      }

      if (this.dontShowClearNotesAgain) {
        this.saveClearNotesPreference({
          skipPrompt: true,
          clearNotes: shouldClear
        });
      }

      this.showClearNotesPrompt = false;
      this.dontShowClearNotesAgain = false;
    },
    getClearNotesPreference() {
      if (typeof window === 'undefined') return null;
      try {
        const raw = window.localStorage.getItem(CLEAR_NOTES_PREF_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (err) {
        console.warn('Failed to parse clear-notes preference:', err);
        return null;
      }
    },
    saveClearNotesPreference(preference) {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(CLEAR_NOTES_PREF_KEY, JSON.stringify(preference));
      } catch (err) {
        console.warn('Failed to save clear-notes preference:', err);
      }
    },
    generateFileName() {
      const now = new Date();
      return now.toLocaleString('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).replace(/\s+/g, '_') + '.pdf';
    }
  }
};
</script>

<style scoped>
.work-area-bg {
  background-image: url('@/assets/work_area_background1.png');
  background-size: 120%;
  background-position: left -12rem;
  background-repeat: no-repeat;
}
.avoid-page-break {
  break-inside: avoid;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
}
</style>
